import WorkorderChecklistFooter from '@/components/Movements/WorkOrders/WorkorderChecklist/ChecklistFooter';
import WorkorderChecklistImagesCreateModal from '@/components/Movements/WorkOrders/WorkorderChecklist/ChecklistImagesCreateModal';
import { useWorkorderChecklistContext } from '@/components/Movements/WorkOrders/WorkorderChecklist/WorkorderChecklistProvider';
import { WorkorderChecklistItemType } from '@/components/Movements/WorkOrders/WorkorderChecklist/WorkorderChecklistProvider/WorkorderChecklistProvider.types';
import { DefaultRouteTabs, Group } from '@/components/_common';
import {
  useWorkOrderChecklistImageList,
  useWorkOrderChecklistImageRemove,
  useWorkOrderChecklistImageRemoveMultiple,
} from '@/core/domain/workorder/WorkOrdersChecklist/WorkOrdersChecklist.hooks';
import { WorkOrderChecklistImage } from '@/core/domain/workorder/WorkOrdersChecklist/WorkOrdersChecklist.types';
import { useDisclosure, useWindowSize } from '@/hooks';
import useChecklistValidRoutes from '@/hooks/useChecklistValidRoutes';
import { PageProps } from '@/types/PageProps';
import { Button, Card, Checkbox, Empty, Image, Modal } from 'antd';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';

import './styles.less';
import { useCallback, useState } from 'react';

const WorkorderChecklistImagesPage: React.FC<PageProps> = ({ route }) => {
  const intl = useIntl();
  const { isMobile, width } = useWindowSize();
  const { checklistItemId, checklistItemType, workorderId, boschChecklist } =
    useWorkorderChecklistContext();
  const { data, isLoading } = useWorkOrderChecklistImageList({
    diagnosticId:
      checklistItemType == WorkorderChecklistItemType.Diagnostic
        ? checklistItemId
        : undefined,
    workOrderComplementId:
      checklistItemType == WorkorderChecklistItemType.Workorder
        ? checklistItemId
        : undefined,
  });
  const images = data?.listChecklistImage;
  const [modalOpened, modalAction] = useDisclosure();

  const routeParent = useChecklistValidRoutes({
    route,
    checklistItemType,
    workorderId,
    boschChecklist,
  });

  const [imageIds, setImageIds] = useState<number[]>([]);

  const removeImage = useWorkOrderChecklistImageRemove();
  const removeMultipleImage = useWorkOrderChecklistImageRemoveMultiple();

  function handleRemove(image: WorkOrderChecklistImage) {
    Modal.confirm({
      title: 'Remover image',
      content: 'Deseja remover a imagem selecionada?',
      okText: 'Remover',
      onOk: () => removeImage.mutateAsync(image.id),
      okType: 'danger',
    });
  }

  const confirmMultipleRemove = useCallback(() => {
    Modal.confirm({
      title: 'Excluir várias imagens',
      content: `Deseja excluir as ${imageIds.length} imagens selecionadas? Não será possível reverter a ação.`,
      okText: 'Excluir',
      onOk: async () => {
        try {
          await removeMultipleImage.mutateAsync({ imageIds });
          setImageIds([]);
        } catch {}
      },
      okButtonProps: { danger: true },
    });
  }, [imageIds]);

  return (
    <DefaultRouteTabs
      route={routeParent}
      baseKey={String(checklistItemId)}
      basePath={`/app/movements/workorderChecklist/${checklistItemId}`}
      extra={
        <Button block type="primary" onClick={modalAction.open}>
          Adicionar imagens
        </Button>
      }
    >
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <Card style={{ marginBottom: 24 }}>
        <Group gap={10}>
          <span style={{ flex: 1 }}>
            Imagens selecionadas: {imageIds.length}/{images?.length} (Máx. 30)
          </span>

          {!!imageIds.length ? (
            <Button onClick={() => setImageIds([])}>Remover seleção</Button>
          ) : (
            <Button
              onClick={() =>
                setImageIds(images?.map((image) => image.id).slice(0, 30) || [])
              }
            >
              Selecionar todos
            </Button>
          )}

          <Button
            danger
            type="primary"
            disabled={!imageIds.length}
            onClick={confirmMultipleRemove}
          >
            Excluir imagens
          </Button>
        </Group>
      </Card>

      <Image.PreviewGroup>
        <Card loading={isLoading}>
          {!images?.length && (
            <Empty description="Não há imagens cadastradas" />
          )}

          {images?.map((image, key) => (
            <Card.Grid
              key={key}
              style={{
                padding: 0,
                width: isMobile ? '100%' : width < 992 ? '50%' : '25%',
              }}
              className="image-card"
              data-selected={imageIds.includes(image.id)}
            >
              <Checkbox
                className="image-selection"
                checked={imageIds.includes(image.id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setImageIds((prev) => {
                      if (prev.length === 30) return prev;
                      return [...prev, image.id];
                    });
                  } else {
                    setImageIds((prev) => prev.filter((id) => image.id !== id));
                  }
                }}
              />

              <Button
                danger
                type="primary"
                className="image-btn-remove"
                onClick={() => handleRemove(image)}
              >
                Remover
              </Button>

              <Image
                src={image.filePath}
                width="100%"
                height={300}
                style={{ objectFit: 'cover' }}
                fallback="data:image/png;base64,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"
              />
            </Card.Grid>
          ))}
        </Card>
      </Image.PreviewGroup>

      <WorkorderChecklistFooter />

      <WorkorderChecklistImagesCreateModal
        open={modalOpened}
        onCancel={modalAction.close}
      />
    </DefaultRouteTabs>
  );
};

export default WorkorderChecklistImagesPage;
